import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ClientLogin.module.css";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";

const ClientLogin = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: ""
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [loginStatus, setLoginStatus] = useState({
    error: false,
    message: ""
  });
  const [isVisible, setIsVisible] = useState(false);
  
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  
  // Get credentials from environment variables (format: username:password)
  const clientCredentials = process.env.REACT_APP_CLIENT_CREDENTIALS;
  const [CLIENT_USERNAME, CLIENT_PASSWORD] = clientCredentials ? clientCredentials.split(':') : [null, null];
  
  // Debug logging
  console.log('Environment variables:', {
    credentials: clientCredentials ? `${CLIENT_USERNAME}:***` : 'undefined',
    nodeEnv: process.env.NODE_ENV,
    validCredentials: !!(CLIENT_USERNAME && CLIENT_PASSWORD)
  });
  
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    const currentSectionRef = sectionRef.current;
    if (currentSectionRef) observer.observe(currentSectionRef);
    
    return () => {
      if (currentSectionRef) observer.unobserve(currentSectionRef);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
    
    // Clear error message when user starts typing
    if (loginStatus.error) {
      setLoginStatus({ error: false, message: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    if (!formData.username || !formData.password) {
      setLoginStatus({
        error: true,
        message: "Please fill in all fields."
      });
      return;
    }

    // Check credentials
    if (formData.username === CLIENT_USERNAME && formData.password === CLIENT_PASSWORD) {
      // Generate unique session token
      const token = btoa(Math.random().toString(36).substring(2) + Date.now().toString(36));
      
      // Store token in session storage (not localStorage so it doesn't persist across browser sessions)
      sessionStorage.setItem('clientAuth', token);
      
      // Redirect to protected dashboard with token in URL
      navigate(`/dashboard/${token}`);
    } else {
      setLoginStatus({
        error: true,
        message: "Invalid username or password."
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className={styles.loginPage}>
      {/* Login Section */}
      <section className={styles.loginSection} ref={sectionRef}>
        <div className={styles.container}>
          <div className={`${styles.loginCard} ${isVisible ? styles.visible : ""}`}>
            <div className={styles.loginHeader}>
              <h2 className={styles.loginTitle}>Login</h2>
            </div>

            <div className={styles.loginContent}>
              <form onSubmit={handleSubmit} className={styles.loginForm}>
                  <div className={styles.formGroup}>
                    <label htmlFor="username" className={styles.formLabel}>
                      Username
                    </label>
                    <div className={styles.inputWrapper}>
                      <FaUser className={styles.inputIcon} />
                      <input
                        type="text"
                        id="username"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        className={styles.formInput}
                        placeholder="Enter your username"
                        required
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="password" className={styles.formLabel}>
                      Password
                    </label>
                    <div className={styles.inputWrapper}>
                      <FaLock className={styles.inputIcon} />
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={styles.formInput}
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        className={styles.togglePassword}
                        onClick={togglePasswordVisibility}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  {loginStatus.error && (
                    <div className={styles.errorMessage}>
                      {loginStatus.message}
                    </div>
                  )}

                  <button type="submit" className={styles.loginButton}>
                    Login
                  </button>
                </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClientLogin; 